import React, { useEffect } from "react";
import { OverviewSectionProps } from "./dashboard.types";
import styles from "./Dashboard.module.css";
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { authenticatedFetch } from "../../utils/authenticatedFetch";

/**
 * Overview Section Component
 *
 * Displays user overview information including credits, next refill date,
 * and brief recent history summary.
 */

const OverviewSection = ({ }: OverviewSectionProps) => {
  const { user, signOut } = useAuth();
  const [credits, setCredits] = useState<number | null>(null);
  const [questionCount, setQuestionCount] = useState<number | null>(null);
  const [threadCount, setThreadCount] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    // Fetch remaining credits -------------------------------------------------
    const loadCredits = async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/credits/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setCredits(data.credits ?? 0);
        }
      } catch (err) {
        console.error("Failed to fetch credits", err);
      }
    };

    // Fetch total questions asked -------------------------------------------
    const loadQuestionCount = async () => {
      try {
        // We only need the count, but the current endpoint returns an array.
        // Request a large limit to ensure we receive all questions.
        const res = await authenticatedFetch(
          `${apiUrl}/api/questions?user_id=${user.id}&limit=10000&offset=0`
        );
        if (res.ok) {
          const data = await res.json();
          const total = Array.isArray(data.questions)
            ? data.questions.length
            : null;
          setQuestionCount(total);
        }
      } catch (err) {
        console.error("Failed to fetch question count", err);
      }
    };

    // Fetch total threads ----------------------------------------------------
    const loadThreadCount = async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/threads/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          const total = Array.isArray(data) ? data.length : null;
          setThreadCount(total);
        }
      } catch (err) {
        console.error("Failed to fetch thread count", err);
      }
    };

    loadCredits();
    loadQuestionCount();
    loadThreadCount();
  }, [user]);

  return (
    <section className={styles.overviewSection}>
      <h3 className={styles.sectionTitle}>Dashboard Overview</h3>
      <p className={styles.sectionDescription}>Here's what's happening with your account today.</p>

      <div className={styles.overviewGrid}>
        {/* Credits Card */}
        <div className={styles.statsCard}>
          <div className={styles.statsIconWrapper} style={{ background: 'rgba(32, 91, 90, 0.1)', color: 'var(--Colors-Primary-600, #2d7f7d)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M224,96a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,96Zm-8,40H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Zm0,48H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16ZM216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V80H40V56H216Z"></path></svg>
          </div>
          <div className={styles.statsContent}>
            <span className={styles.statsLabel}>Available Credits</span>
            <span className={styles.statsValue}>{credits ?? "-"}</span>
          </div>
        </div>

        {/* Questions Card */}
        <div className={styles.statsCard}>
          <div className={styles.statsIconWrapper} style={{ background: 'rgba(250, 173, 51, 0.1)', color: 'var(--Colors-Secondary-600, #f89b07)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M216,48H40A16,16,0,0,0,24,64V224a15.84,15.84,0,0,0,9.25,14.5A16.05,16.05,0,0,0,40,240a15.89,15.89,0,0,0,10.25-3.78l.09-.07L83,208H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48ZM92,120a12,12,0,1,1,12-12A12,12,0,0,1,92,120Zm36,0a12,12,0,1,1,12-12A12,12,0,0,1,128,120Zm36,0a12,12,0,1,1,12-12A12,12,0,0,1,164,120Z"></path></svg>
          </div>
          <div className={styles.statsContent}>
            <span className={styles.statsLabel}>Questions Asked</span>
            <span className={styles.statsValue}>{questionCount ?? "-"}</span>
          </div>
        </div>

        {/* Usage Stats Card */}
        <div className={styles.statsCard}>
          <div className={styles.statsIconWrapper} style={{ background: 'rgba(159, 65, 65, 0.1)', color: 'var(--Colors-Accent-600, #9f4141)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm64-88a8,8,0,0,1-8,8H128a8,8,0,0,1-8-8V72a8,8,0,0,1,16,0v48h48A8,8,0,0,1,192,128Z"></path></svg>
          </div>
          <div className={styles.statsContent}>
            <span className={styles.statsLabel}>Total Threads</span>
            <span className={styles.statsValue}>
              {threadCount ?? "-"}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.sectionDivider}></div>

      {/* Brief History / Activity Section */}
      <div className={styles.activityContainer}>
        <div className={styles.activityHeader}>
          <h4 className={styles.subsectionTitle}>Recent Activity</h4>
          <button className={styles.viewAllButton} onClick={() => window.location.href = '/dashboard?tab=history'}>View Full History</button>
        </div>

        <div className={styles.activityCard}>
          {(questionCount !== null && questionCount > 0) ? (
            <div className={styles.emptyActivityState}>
              <div className={styles.activityStat}>
                <span className={styles.activityStatValue}>{questionCount}</span>
                <span className={styles.activityStatLabel}>Total Questions</span>
              </div>
              <div className={styles.activityStatDivider}></div>
              <div className={styles.activityStat}>
                <span className={styles.activityStatValue}>{threadCount}</span>
                <span className={styles.activityStatLabel}>Active Threads</span>
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No activity yet. Start a chat to see your stats here!</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default OverviewSection;
